import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { CircleAlert } from "lucide-react";
import CustomModal from "@/components/modal/CustomModal";

// Zod 스키마 정의
const deleteConfirmSchema = z.object({
  confirmText: z.string().refine((val) => val === "동의합니다", {
    message: "동의 문장 입력이 올바르지 않습니다.",
  }),
});

type DeleteConfirmForm = z.infer<typeof deleteConfirmSchema>;

interface DeleteConfirmModalProps {
  isOpen: boolean;
  memberName: string;
  groupName?: string;
  onConfirm: () => void;
  onCancel: () => void;
  isPending?: boolean;
}

export default function DeleteConfirmModal({
  isOpen,
  memberName,
  groupName,
  onConfirm,
  onCancel,
  isPending = false,
}: DeleteConfirmModalProps) {
  const methods = useForm<DeleteConfirmForm>({
    resolver: zodResolver(deleteConfirmSchema),
    mode: "onChange",
  });

  const {
    register,
    handleSubmit,
    formState: { isValid },
    watch,
  } = methods;

  const confirmText = watch("confirmText");

  if (!isOpen) return null;

  const onSubmit = (data: DeleteConfirmForm) => {
    if (data.confirmText === "동의합니다") {
      onConfirm();
    }
  };

  return (
    <CustomModal.Panel
      onClose={onCancel}
      dismissOnOverlay={!isPending}
      className="w-full max-w-md rounded-[10px] bg-white shadow-lg"
    >
      <div className="-m-5 flex flex-col items-center justify-center p-6">
        {/* 헤더 */}
        <div className="mb-4 flex items-center justify-center relative w-full">
          <div className="flex items-center gap-2">
            <CircleAlert className="text-[#ED1C24]" />
            <h2 className="font-pretendard text-[18px] font-bold text-text-main2">
              근무자 삭제
            </h2>
          </div>
          <button
            onClick={() => !isPending && onCancel()}
            disabled={isPending}
            className="absolute right-[-8px] top-[-8px] text-gray-400 hover:text-gray-600 disabled:cursor-not-allowed text-[20px]"
          >
            <span className="text-[20px]">✕</span>
          </button>
        </div>

        {/* 안내 문구 */}
        <p className="mb-4 text-[16px] text-text-main2 font-pretendard">
          {groupName ? (
            <span className="font-bold">{groupName}</span>
          ) : (
            "해당 병동"
          )}
          에서 <span className="font-bold">{memberName}</span> 근무자를
          삭제할까요?
        </p>

        {/* 경고 메시지 */}
        <div className="mb-4 bg-[#FFF0F0] p-4 border border-[#ED1C24]">
          <p className="text-[16px] text-text-main2 font-pretendard">
            삭제된 근무자는 (근무자 추가)하여 재등록이 가능하지만,
            <br />
            히스토리(수정/변경사항)는 복구할 수 없습니다.
          </p>
        </div>

        {/* Form */}
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            {/* 입력 안내 */}
            <p className="mb-2 text-center text-[16px] text-text-main2 font-pretendard">
              주의사항 확인 후 <span className="font-bold">'동의합니다'</span>를
              입력해주세요.
            </p>

            {/* 입력 필드 */}
            <div className="mb-2 flex items-center justify-center">
              <input
                {...register("confirmText")}
                type="text"
                placeholder="동의합니다"
                className="placeholder:text-[#C2C2C2] rounded-[7px] border px-4 py-2 text-[16px] border-[#ED1C24] w-[186px]"
              />
            </div>

            {/* 에러 메시지 */}
            {/* {errors.confirmText && (
                            <p className="mb-4 text-center text-xs text-red-500">
                                {errors.confirmText.message}
                            </p>
                        )} */}

            <p className="mb-4 text-center text-[13px] font-pretendard text-[#ED1C24]">
              동의 항목 입력은 필수입니다.
            </p>

            {/* 버튼 */}
            <div className="flex gap-[11px] justify-center">
              <button
                type="button"
                onClick={() => !isPending && onCancel()}
                disabled={isPending}
                className="flex justify-center items-center w-[85px] h-[30px] rounded-full border border-gray-300 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                취소
              </button>
              <button
                type="submit"
                disabled={!isValid || confirmText !== "동의합니다" || isPending}
                className="flex justify-center items-center rounded-full bg-[#FFF0F0] py-2 disabled:cursor-not-allowed disabled:opacity-40 w-[85px] h-[30px] border border-[#ED1C24]"
              >
                <span className="font-pretendard text-[14px] text-[#ED1C24]">
                  {isPending ? "삭제 중..." : "삭제"}{" "}
                </span>
              </button>
            </div>
          </form>
        </FormProvider>
      </div>
    </CustomModal.Panel>
  );
}
